import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {SequenceSchemaType, TagSchemaSupportedFolderItemType} from 'constants/RegistryConstants';
import {TagSchemaSupportedFolderItemType as TagSchemaSupportedFolderItemTypeGQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FONT_PROXIMA_13, FONT_PROXIMA_14} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARK, NEAR_BLACK} from 'ui-library/Colors';
import SelectSearchableDropdown from 'ui-library/dropdowns/SelectSearchableDropdown';
import {OptionData} from 'ui-library/menus/MenuConstants';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import {
  DraftImplementedInterfaceWithMappedFields,
  StaticSchemaInterfaceFieldName,
} from './StaticSchemaInterfaceConstants';

export interface StaticSchemaInterfaceFieldDefinition {
  key: StaticSchemaInterfaceFieldName;
  displayName: string;
  placeholder: string;
  canMapToMultipleValues: boolean;
  isRequired?: boolean;
  schemaCriteria?: SchemaCriteria;
}

interface SchemaCriteria {
  // If allowedFolderItemTypes == null, then all folder item types are allowed.
  allowedFolderItemTypes?: Array<
    TagSchemaSupportedFolderItemType | TagSchemaSupportedFolderItemTypeGQL | SequenceSchemaType
  >;
}

function schemaMatchesAllowedFolderItemTypes(
  schema: TagSchemaSummaryGraphQL,
  allowedFolderItemTypes?: SchemaCriteria['allowedFolderItemTypes']
): boolean {
  if (allowedFolderItemTypes == null) {
    return true;
  }

  return (
    (allowedFolderItemTypes as Array<string>).includes(schema.folderItemType) ||
    (schema.sequenceType != null && allowedFolderItemTypes.includes(schema.sequenceType))
  );
}

export function schemaMatchesSchemaCriteria(
  schema: TagSchemaSummaryGraphQL,
  criteria?: SchemaCriteria
): boolean {
  return schemaMatchesAllowedFolderItemTypes(schema, criteria?.allowedFolderItemTypes);
}

function getSchemaOptions(
  schemas: Array<TagSchemaSummaryGraphQL>,
  criteria?: SchemaCriteria
): Array<OptionData> {
  const schemaOptions = schemas
    .filter((schema) => schemaMatchesSchemaCriteria(schema, criteria))
    .map((schema) => ({
      name: schema.name,
      value: schema.id,
      disabled: isArchived(schema),
      disabledMessage: isArchived(schema) ? 'Schema is archived' : undefined,
    }));
  // Sort disabled options to the back.
  schemaOptions.sort((a, b) => {
    if (a.disabled < b.disabled) {
      return -1;
    } else if (a.disabled > b.disabled) {
      return 1;
    } else {
      return 0;
    }
  });
  return schemaOptions;
}

interface StaticSchemaInterfaceSystemFieldDropdownProps {
  disabled: boolean;
  onSelect: (values: Array<string>) => void;
  staticFieldDefinition: StaticSchemaInterfaceFieldDefinition;
  staticFieldValue: Array<string> | string | null;
  optionDatas: Array<OptionData>;
}

export function StaticSchemaInterfaceSystemFieldDropdown(
  props: StaticSchemaInterfaceSystemFieldDropdownProps
): JSX.Element {
  return (
    <div className={css(styles.dropdown)}>
      <div className={css(styles.fieldName)}>
        {props.staticFieldDefinition.displayName}
        {props.staticFieldDefinition.isRequired && <span className="u-danger-color">*</span>}
      </div>
      <SelectSearchableDropdown
        disabled={props.disabled}
        menuProps={{isMulti: props.staticFieldDefinition.canMapToMultipleValues}}
        noneSelectedMessage={
          <div className={css(styles.noneSelectedMessage)}>{props.staticFieldDefinition.placeholder}</div>
        }
        optionDatas={props.optionDatas}
        onSelect={props.onSelect}
        selectedValues={
          typeof props.staticFieldValue === 'string'
            ? [props.staticFieldValue]
            : (props.staticFieldValue ?? [])
        }
        closeOnInsideClick={!props.staticFieldDefinition.canMapToMultipleValues}
        dataTestAttrs={{
          component: 'StaticSchemaInterfaceSystemFieldDropdown',
          element: 'selectSearchableDropdown',
          key: props.staticFieldDefinition.key,
        }}
      />
    </div>
  );
}

export interface StaticInterfaceField {
  fieldDefinition: StaticSchemaInterfaceFieldDefinition;
  values: Array<string> | null;
}

export interface StaticSchemaInterfaceFieldMappingProps {
  disabled: boolean;
  onChange: (newStaticInterfaceField: StaticInterfaceField) => void;
  staticFields: Array<StaticInterfaceField>;
  // Incomplete implementation of a static schema interface, to track partial state as mappings are configured.
  implementedStaticSchemaInterface: DraftImplementedInterfaceWithMappedFields;
  subtitle: JSX.Element;
  title: JSX.Element;
  schemas: Array<TagSchemaSummaryGraphQL>;
}

/** Handles mapping from internal Benchling static interface fields to customer schemas.
 *
 * May be used by static schema interfaces that:
 * - Have interface field definitions.
 * - Expect each user configure the interface on a schema by mapping these interface field definitions to
 *   other schemas.
 *
 * The mappings may be one to one or one to many (StaticSchemaInterfaceFieldDefinition.canMapToMultipleValues).
 * The static interface should implement a few other prerequisites on the FE and BE:
 * - The interface field definitions to be provided as props.
 * - Parsing and validation code on the backend, via @register_implemented_static_interface_params
 *   and subclassing StaticSchemaInterfaceHandlerBase.
 *
 * Historical note: At one point, this component expected interface fields to map to schema fields instead of
 * schemas. Then the needs of the only project using this component at that time changed, and we
 * decided to just remove the schema field mapping feature since there were now no usages of it.
 * This was done in https://github.com/benchling/aurelia/pull/112199.
 * Last commit hash with the schema field mapping: d977158e4b8ad91b332ef4fbb23ba870706ae673
 */
export default function StaticSchemaInterfaceFieldMapping(
  props: StaticSchemaInterfaceFieldMappingProps
): JSX.Element {
  const onSelect = (newStaticInterfaceField: StaticInterfaceField): void => {
    const fieldDefinition = newStaticInterfaceField.fieldDefinition;
    const values = newStaticInterfaceField.values;

    if (values != null && values.length > 1 && !fieldDefinition.canMapToMultipleValues) {
      const currentValues =
        props.staticFields.find((f) => f.fieldDefinition.key === fieldDefinition.key)?.values ?? [];
      const newValues = values.filter((v) => v !== currentValues[0]);
      assert(newValues.length <= 1, 'Attempted to select more than one value on a single value field');
      props.onChange({...newStaticInterfaceField, values: newValues});
    } else {
      props.onChange(newStaticInterfaceField);
    }
  };

  return (
    <div
      className={css(styles.mapping)}
      {...getTestProps({component: 'StaticSchemaInterfaceFieldMapping', element: 'root'})}
    >
      <div className={css(styles.title)}>{props.title}</div>
      <div className={css(styles.subtitle)}>{props.subtitle}</div>
      <div className={css(styles.table)}>
        {props.staticFields.map((staticField) => (
          <StaticSchemaInterfaceSystemFieldDropdown
            disabled={props.disabled}
            key={staticField.fieldDefinition.key}
            onSelect={(selectedValues) => {
              onSelect({fieldDefinition: staticField.fieldDefinition, values: selectedValues});
            }}
            optionDatas={getSchemaOptions(props.schemas, staticField.fieldDefinition.schemaCriteria)}
            staticFieldDefinition={staticField.fieldDefinition}
            staticFieldValue={staticField.values}
          />
        ))}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  mapping: {
    marginBottom: '32px',
  },
  title: {
    ...FONT_PROXIMA_14,
    color: NEAR_BLACK,
  },
  subtitle: {
    ...FONT_PROXIMA_13,
    color: NEAR_BLACK,
  },
  table: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: '11px',
  },
  dropdown: {
    width: '50%',
  },
  fieldName: {
    ...FONT_PROXIMA_13,
    color: NEAR_BLACK,
  },
  noneSelectedMessage: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARK,
  },
});
